import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Tooltip,
  Stack,
  Fade,
  Paper,
  Menu,
  MenuList,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Switch,
  Popover,
  Alert
} from '@mui/material';
import {
  DragIndicator as DragIcon,
  GridView as GridIcon,
  Save as SaveIcon,
  Undo as UndoIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  Public as PublicIcon,
  PlaylistAdd as CreateCategoryIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  Tune as TuneIcon
} from '@mui/icons-material';
import useIsMobileDevice from '../../hooks/useIsMobileDevice';
import useSyncPreference from '../../hooks/useSyncPreference';
import { setPersonalLayoutSameAsDefault } from '../../services/layout.service';

// const EXPAND_WIDTH = "280px";
// const NOT_EXPAND_WIDTH = "79px";

const layoutMenuPaperSx = {
  borderRadius: 2,
  minWidth: 280,
  mt: 1,
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
  overflow: 'hidden',
  '& .MuiMenuItem-root': {
    py: 1.25,
    minHeight: 48,
  },
};

const layoutMenuListItemTextSecondaryProps = {
  variant: 'caption',
  sx: { color: 'text.secondary', lineHeight: 1.35 },
};

const LayoutController = ({
  isLayoutMode,
  onToggleLayoutMode,
  onSaveLayout,
  onResetLayout,
  onLayoutTypeChange,
  onSetSameAsDefault, // New prop for setting layout same as default
  isPersonalSameAsDefault = false, // New prop to track if personal layout should match default
  onTogglePersonalSameAsDefault, // New prop to handle toggle
  currentLayoutType = 'personal', // 'personal' or 'default'
  hasChanges = false,
  disabled = false,
  pageType, // Add pageType prop to fetch sync preference
  onPageChange, // New prop to handle page changes and auto-sync
  onCreateCategory, // New prop to create a category
  onAddCategoryFromMenu, // Special menu-only add-category flow
}) => {
  const [saving, setSaving] = useState(false);
  const [settingSameAsDefault, setSettingSameAsDefault] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [confirmationAnchorEl, setConfirmationAnchorEl] = useState(null);
  const [pendingToggleValue, setPendingToggleValue] = useState(null);
  const [autoSyncing, setAutoSyncing] = useState(false);
  const [resetConfirmationAnchorEl, setResetConfirmationAnchorEl] = useState(null);
  const [saveForBothLayoutsOnNextSave, setSaveForBothLayoutsOnNextSave] = useState(false);
  const [customizeSubmenuAnchor, setCustomizeSubmenuAnchor] = useState(null);
  const customizeSubmenuCloseTimerRef = useRef(null);
  const currentLayoutTypeRef = useRef(currentLayoutType);
  const isLayoutModeRef = useRef(isLayoutMode);

  // Use the custom hook for sync preference management
  const {
    syncPreference = false,
    loading: loadingSyncPreference = false,    
    isSuperUser = false,
    toggleSyncPreference: toggleSyncPreferenceHandler
  } = useSyncPreference(pageType) || {};
  
  const isMobile = useIsMobileDevice() || false;

  // Helper function to get pageType from prop or route
  const getPageType = useCallback(() => {
    if (pageType) return pageType;

    const currentPath = window.location.pathname;
    if (currentPath.includes('/hospital')) {
      return 'hospital';
    } else if (currentPath.includes('/admission')) {
      return 'admission';
    } else if (currentPath.includes('/community-transfer')) {
      return 'communityTransfer';
    } else if (currentPath.includes('/deceased')) {
      return 'deceased';
    } else if (currentPath.includes('/overall')) {
      return 'overall';
    }

    return null;
  }, [pageType]);

  // Function to handle auto-sync when syncWithDefault is enabled
  const handleAutoSync = useCallback(async (currentPageType) => {
    if (!isSuperUser || !currentPageType) return;

    try {
      setAutoSyncing(true);

      // Call the set-same-as-default API
      await setPersonalLayoutSameAsDefault(currentPageType);

      // Notify parent component about the page change and sync
      if (onPageChange) {
        onPageChange(currentPageType, true); // true indicates sync was performed
      }
    } catch (error) {
      console.error('Error during auto-sync:', error);
      // Notify parent component about the page change without sync
      if (onPageChange) {
        onPageChange(currentPageType, false); // false indicates no sync was performed
      }
    } finally {
      setAutoSyncing(false);
    }
  }, [isSuperUser, onPageChange]);

  // Auto-sync when syncPreference changes to true
  useEffect(() => {
    if (syncPreference && isSuperUser) {
      const actualPageType = getPageType();
      if (actualPageType) {
        handleAutoSync(actualPageType);
      }
    }
  }, [syncPreference, isSuperUser, handleAutoSync, getPageType]);

  useEffect(() => {
    currentLayoutTypeRef.current = currentLayoutType;
  }, [currentLayoutType]);

  useEffect(() => {
    isLayoutModeRef.current = isLayoutMode;
  }, [isLayoutMode]);

  useEffect(() => {
    return () => {
      if (customizeSubmenuCloseTimerRef.current) {
        clearTimeout(customizeSubmenuCloseTimerRef.current);
      }
    };
  }, []);

  const waitUntilDefaultLayoutActive = useCallback(async (timeoutMs = 3000) => {
    const startedAt = Date.now();
    while (Date.now() - startedAt < timeoutMs) {
      if (currentLayoutTypeRef.current === 'default' && isLayoutModeRef.current) {
        return true;
      }
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
    return currentLayoutTypeRef.current === 'default' && isLayoutModeRef.current;
  }, []);

  const clearCustomizeSubmenuCloseTimer = () => {
    if (customizeSubmenuCloseTimerRef.current) {
      clearTimeout(customizeSubmenuCloseTimerRef.current);
      customizeSubmenuCloseTimerRef.current = null;
    }
  };

  const scheduleCustomizeSubmenuClose = () => {
    clearCustomizeSubmenuCloseTimer();
    customizeSubmenuCloseTimerRef.current = setTimeout(() => {
      setCustomizeSubmenuAnchor(null);
      customizeSubmenuCloseTimerRef.current = null;
    }, 200);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSaveLayout({ saveForBothLayouts: saveForBothLayoutsOnNextSave });
      if (saveForBothLayoutsOnNextSave) {
        setSaveForBothLayoutsOnNextSave(false);
      }
    } finally {
      setSaving(false);
    }
  };

  // const handleSetSameAsDefault = async () => {
  //   if (!onSetSameAsDefault) return;

  //   setSettingSameAsDefault(true);
  //   try {
  //     await onSetSameAsDefault();
  //   } finally {
  //     setSettingSameAsDefault(false);
  //   }
  // };

  const handleToggleLayoutMode = () => {
    onToggleLayoutMode();
  };

  const handleMenuClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setCustomizeSubmenuAnchor(null);
    clearCustomizeSubmenuCloseTimer();
  };

  const handleAddCategoryFromMenu = async () => {
    handleMenuClose();

    if (typeof onAddCategoryFromMenu === 'function') {
      setSaveForBothLayoutsOnNextSave(true);
      try {
        await onAddCategoryFromMenu();
      } catch (error) {
        console.error('Error in menu add-category flow:', error);
      }
      return;
    }

    let switchedViaLayoutType = false;
    try {
      if (onLayoutTypeChange) {
        await onLayoutTypeChange('default');
        switchedViaLayoutType = true;
        await waitUntilDefaultLayoutActive();
      }
      setSaveForBothLayoutsOnNextSave(true);
    } catch (error) {
      console.error('Error preparing add category flow:', error);
    }

    if (!switchedViaLayoutType && onToggleLayoutMode && !isLayoutMode) {
      onToggleLayoutMode();
    }

    if (onCreateCategory) {
      onCreateCategory();
    }
  };

  const handleLayoutTypeSelect = (layoutType) => {
    handleMenuClose();
    if (onLayoutTypeChange) {
      onLayoutTypeChange(layoutType);
    }
  };

  const handlePersonalSameAsDefaultToggle = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const newValue = !syncPreference;
    setPendingToggleValue(newValue);
    setConfirmationAnchorEl(event.currentTarget);
  };

  const handleConfirmationClose = () => {
    setConfirmationAnchorEl(null);
    setPendingToggleValue(null);
  };

  const handleConfirmToggle = async () => {
    setConfirmationAnchorEl(null);

    const actualPageType = getPageType();

    // Check if pageType is available
    if (!actualPageType) {
      console.error('Could not determine pageType from route. Please provide pageType prop.');
      return;
    }

    setSettingSameAsDefault(true);
    try {
      if (onTogglePersonalSameAsDefault) {
        await onTogglePersonalSameAsDefault(pendingToggleValue);
      } else {
        await toggleSyncPreferenceHandler(pendingToggleValue);
      }

      // If enabling sync preference, trigger auto-sync immediately
      if (pendingToggleValue) {
        await handleAutoSync(actualPageType);
      }
    } catch (error) {
      console.error('Error in handleConfirmToggle:', error);
      // Don't update local state if API call failed
    } finally {
      setSettingSameAsDefault(false);
      setPendingToggleValue(null);
    }
  };

  const handleCancelToggle = () => {
    setConfirmationAnchorEl(null);
    setPendingToggleValue(null);
    // Don't update local state - keep it as it was
  };

  // Reset layout confirmation handlers
  const handleResetLayoutClick = (event) => {
    setResetConfirmationAnchorEl(event.currentTarget);
  };

  const handleResetConfirmationClose = () => {
    setResetConfirmationAnchorEl(null);
  };

  const handleConfirmReset = () => {
    setResetConfirmationAnchorEl(null);
    onResetLayout();
  };

  const handleCancelReset = () => {
    setResetConfirmationAnchorEl(null);
  };

  // Safety check to prevent rendering issues
  if (typeof isLayoutMode === 'undefined' || typeof isMobile === 'undefined') {
    return null;
  }

  if (!isLayoutMode && !isMobile) {
    return (
      <Box sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 1000,
        display: { xs: 'none', sm: 'block' }
      }}>
        {isSuperUser ? (
          <>
            <Tooltip title="Customize card layout" arrow>
              <IconButton
                onClick={handleMenuClick}
                disabled={disabled}
                sx={{
                  backgroundColor: 'primary.main',
                  color: 'white',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                    transform: 'scale(1.05)',
                  },
                  '&:disabled': {
                    backgroundColor: 'grey.400',
                    color: 'grey.600',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <DragIcon />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={menuAnchorEl || null}
              open={Boolean(menuAnchorEl)}
              onClose={handleMenuClose}
              disableScrollLock
              keepMounted
              MenuListProps={{
                dense: false,
                'aria-label': 'Layout options',
              }}
              PaperProps={{
                elevation: 8,
                sx: layoutMenuPaperSx,
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem
                id="layout-customize-trigger"
                aria-haspopup="true"
                aria-expanded={Boolean(customizeSubmenuAnchor)}
                aria-controls="layout-customize-submenu"
                onMouseEnter={(e) => {
                  clearCustomizeSubmenuCloseTimer();
                  setCustomizeSubmenuAnchor(e.currentTarget);
                }}
                onMouseLeave={scheduleCustomizeSubmenuClose}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  clearCustomizeSubmenuCloseTimer();
                  setCustomizeSubmenuAnchor(e.currentTarget);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    clearCustomizeSubmenuCloseTimer();
                    setCustomizeSubmenuAnchor(e.currentTarget);
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <TuneIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Customize layout"
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: 500,
                    color: 'text.primary',
                  }}
                  sx={{ flex: '1 1 auto', pr: 0.5 }}
                />
                <KeyboardArrowRightIcon fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0 }} />
              </MenuItem>
              {typeof onCreateCategory === 'function' && (
                <Divider key="layout-add-category-divider" component="li" sx={{ my: 0 }} />
              )}
              {typeof onCreateCategory === 'function' && (
                <MenuItem key="layout-add-category" onClick={handleAddCategoryFromMenu} disabled={disabled}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CreateCategoryIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Add category"
                    secondary="Open layout mode and add a new group"
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={layoutMenuListItemTextSecondaryProps}
                  />
                </MenuItem>
              )}
            </Menu>
            <Popover
              open={Boolean(customizeSubmenuAnchor)}
              anchorEl={customizeSubmenuAnchor}
              onClose={() => setCustomizeSubmenuAnchor(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              disableScrollLock
              disableRestoreFocus
              PaperProps={{
                elevation: 8,
                sx: {
                  ...layoutMenuPaperSx,
                  mt: 0,
                  mr: 0.5,
                  minWidth: 300,
                  py: 0.5,
                  pointerEvents: 'auto',
                },
              }}
              sx={{ pointerEvents: 'none' }}
            >
              <MenuList
                id="layout-customize-submenu"
                dense
                autoFocusItem={false}
                onMouseEnter={clearCustomizeSubmenuCloseTimer}
                onMouseLeave={scheduleCustomizeSubmenuClose}
                aria-labelledby="layout-customize-trigger"
              >
                <MenuItem
                  onClick={handleLayoutTypeSelect.bind(null, 'personal')}
                  disabled={settingSameAsDefault || loadingSyncPreference || autoSyncing}
                  sx={{
                    alignItems: 'flex-start',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                    <PersonIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Own layout"
                    secondary={
                      autoSyncing
                        ? 'Auto-syncing with default layout...'
                        : 'Save layout for your user account'
                    }
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={layoutMenuListItemTextSecondaryProps}
                  />
                  <Switch
                    checked={syncPreference}
                    onChange={handlePersonalSameAsDefaultToggle}
                    disabled={settingSameAsDefault || loadingSyncPreference || autoSyncing}
                    size="small"
                    onClick={(e) => e.stopPropagation()}
                    sx={{ mt: 0.25 }}
                  />
                </MenuItem>
                <Divider component="li" sx={{ my: 0 }} />
                <MenuItem onClick={() => handleLayoutTypeSelect('default')}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <PublicIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Default layout"
                    secondary="Save as default for all users"
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={layoutMenuListItemTextSecondaryProps}
                  />
                </MenuItem>
              </MenuList>
            </Popover>

            {/* Confirmation Popover */}
            <Popover
              open={Boolean(confirmationAnchorEl)}
              anchorEl={confirmationAnchorEl || null}
              onClose={handleConfirmationClose}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              PaperProps={{
                elevation: 8,
                sx: {
                  p: 2,
                  minWidth: 300,
                },
              }}
            >
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="medium">
                  {pendingToggleValue
                    ? 'Enable Auto-Sync with Default Layout?'
                    : 'Disable Auto-Sync with Default Layout?'
                  }
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                  {pendingToggleValue
                    ? `Your personal layout will automatically match the default layout. 
                    Any changes to the default layout will be reflected in your personal layout. 
                    This will also sync your current layout immediately.`
                    : 'Your personal layout will be independent of the default layout. You can customize it separately.'
                  }
                </Typography>
              </Alert>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  size="small"
                  onClick={handleCancelToggle}
                  disabled={settingSameAsDefault || autoSyncing}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleConfirmToggle}
                  disabled={settingSameAsDefault || autoSyncing}
                >
                  {settingSameAsDefault || autoSyncing ? 'Updating...' : 'Confirm'}
                </Button>
              </Stack>
            </Popover>

            {/* Reset Layout Confirmation Popover */}
            <Popover
              open={Boolean(resetConfirmationAnchorEl)}
              anchorEl={resetConfirmationAnchorEl || null}
              onClose={handleResetConfirmationClose}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              PaperProps={{
                elevation: 8,
                sx: {
                  p: 2,
                  minWidth: 280,
                  maxWidth: 320,
                },
              }}
            >
              <Box>
                <Typography variant="h6" gutterBottom>
                  Reset Layout
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Are you sure you want to reset the layout to default? This action cannot be undone.
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    size="small"
                    onClick={handleCancelReset}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    size="small"
                    onClick={handleConfirmReset}
                  >
                    Reset Layout
                  </Button>
                </Stack>
              </Box>
            </Popover>
          </>
        ) : (
          <Tooltip title="Customize card layout" arrow>
            <IconButton
              onClick={handleToggleLayoutMode}
              disabled={disabled}
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                  transform: 'scale(1.05)',
                },
                '&:disabled': {
                  backgroundColor: 'grey.400',
                  color: 'grey.600',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <DragIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }

     return (
     <>
       <Fade in={isLayoutMode}>
         <Paper
           elevation={8}
           sx={{
             position: 'fixed',
             bottom: 24,
             left: '50%',
             transform: 'translateX(-50%)',
             zIndex: 1000,
             backgroundColor: 'background.paper',
             borderRadius: 2,
             p: 2,
             minWidth: 300,
             boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
             display: { xs: 'none', sm: 'block' }
           }}
         >
           <Stack direction="row" spacing={2} alignItems="center">
             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
               <GridIcon color="primary" />
               <Typography variant="body2" fontWeight="medium">
                 Layout Mode
               </Typography>
               {isSuperUser && (
                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                   <SettingsIcon fontSize="small" color="action" />
                   <Typography variant="caption" color="text.secondary">
                     {currentLayoutType === 'default' ? 'Default' : 'Personal'}
                   </Typography>
                 </Box>
               )}
             </Box>

             <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
               <Tooltip title="Reset to default layout" arrow>
                 <IconButton
                   onClick={handleResetLayoutClick}
                   disabled={disabled}
                   size="small"
                   sx={{
                     '&:hover': {
                       backgroundColor: 'warning.light',
                       color: 'warning.contrastText',
                     },
                   }}
                 >
                   <UndoIcon fontSize="small" />
                 </IconButton>
               </Tooltip>
              <Tooltip title="Create new category" arrow>
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={onCreateCategory}
                    disabled={disabled}
                    startIcon={<CreateCategoryIcon fontSize="small" />}
                    sx={{
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: 'primary.light',
                        color: 'primary.contrastText',
                        borderColor: 'primary.light',
                      },
                    }}
                  >
                    New category
                  </Button>
                </span>
              </Tooltip>

               <Button
                 variant="contained"
                 size="small"
                 onClick={handleSave}
                 disabled={!hasChanges || saving || disabled}
                 startIcon={<SaveIcon />}
                 sx={{
                   minWidth: 80,
                   textTransform: 'none',
                 }}
               >
                 {saving ? 'Saving...' : 'Save'}
               </Button>

               <Tooltip title="Exit layout mode" arrow>
                 <IconButton
                   onClick={handleToggleLayoutMode}
                   disabled={disabled}
                   size="small"
                   sx={{
                     '&:hover': {
                       backgroundColor: 'grey.200',
                     },
                   }}
                 >
                   <CloseIcon fontSize="small" />
                 </IconButton>
               </Tooltip>
             </Stack>
           </Stack>

           <Typography
             variant="caption"
             display="block"
             sx={{
               mt: 1,
               color: 'text.secondary',
               textAlign: 'center'
             }}
           >
            {isSuperUser
              ? `Drag cards to rearrange • Drag category panels and cards in the grid • Saving as ${currentLayoutType === 'default' ? 'default layout' : 'personal layout'}`
              : 'Drag cards to rearrange • Drag category panels and cards in the grid • Changes auto-save when you exit'
            }
           </Typography>
           <Typography
             variant="caption"
             display="block"
             sx={{
               mt: 0.5,
               color: 'text.secondary',
               textAlign: 'center',
               fontStyle: 'italic',
               opacity: 0.8
             }}
           >
             Note: Dashboard previews use placeholder data.
           </Typography>
         </Paper>
       </Fade>

       {/* Reset Layout Confirmation Popover for Layout Mode */}
       <Popover
         open={Boolean(resetConfirmationAnchorEl)}
         anchorEl={resetConfirmationAnchorEl || null}
         onClose={handleResetConfirmationClose}
         anchorOrigin={{
           vertical: 'top',
           horizontal: 'center',
         }}
         transformOrigin={{
           vertical: 'bottom',
           horizontal: 'center',
         }}
         PaperProps={{
           elevation: 8,
           sx: {
             p: 2,
             minWidth: 280,
             maxWidth: 320,
           },
         }}
       >
         <Box>
           <Typography variant="h6" gutterBottom>
             Reset Layout
           </Typography>
           <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
             Are you sure you want to reset the layout to default? This action cannot be undone.
           </Typography>
           <Stack direction="row" spacing={1} justifyContent="flex-end">
             <Button
               size="small"
               onClick={handleCancelReset}
             >
               Cancel
             </Button>
             <Button
               variant="contained"               
               size="small"
               onClick={handleConfirmReset}
             >
               Reset Layout
             </Button>
           </Stack>
         </Box>
       </Popover>
     </>
   );
};

export default LayoutController; 